import React from "react";
import {
  Heart,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from "lucide-react";
import { doctorInfo } from "../mockData";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: "Home", id: "hero" },
    { label: "About", id: "about" },
    { label: "Services", id: "services" },
    { label: "Experience", id: "experience" },
  ];

  const resources = [
    { label: "Testimonials", id: "testimonials" },
    { label: "Publications", id: "publications" },
    { label: "Gallery", id: "gallery" },
    { label: "Contact", id: "contact" },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-500 p-2 rounded-xl">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                Dr. Srivastav
              </span>
            </div>
            <p className="text-gray-300">
              Leading transplant pulmonologist providing life-saving care in
              Telangana and Andhra Pradesh.
            </p>
            {/* Social media */}
            <div className="flex space-x-3 pt-4">
              <a
                href="#"
                className="bg-gradient-to-br from-cyan-500 to-blue-500 p-2 rounded-lg hover:scale-110 transition-transform duration-300"
              >
                <Facebook className="h-5 w-5 text-white" />
              </a>
              <a
                href="#"
                className="bg-gradient-to-br from-blue-500 to-indigo-500 p-2 rounded-lg hover:scale-110 transition-transform duration-300"
              >
                <Twitter className="h-5 w-5 text-white" />
              </a>
              <a
                href="#"
                className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-lg hover:scale-110 transition-transform duration-300"
              >
                <Instagram className="h-5 w-5 text-white" />
              </a>
              <a
                href="#"
                className="bg-gradient-to-br from-cyan-500 to-teal-500 p-2 rounded-lg hover:scale-110 transition-transform duration-300"
              >
                <Linkedin className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-300 hover:text-cyan-300 transition-colors duration-200 hover:translate-x-1 transform inline-block"
                  >
                    → {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
              Resources
            </h3>
            <ul className="space-y-2">
              {resources.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-300 hover:text-purple-300 transition-colors duration-200 hover:translate-x-1 transform inline-block"
                  >
                    → {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-pink-300 to-cyan-300 bg-clip-text text-transparent">
              Contact
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <MapPin className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">
                  {doctorInfo.address}
                </span>
              </li>
              <li>
                <a
                  href={`tel:${doctorInfo.phone}`}
                  className="flex items-center space-x-2 text-gray-300 hover:text-cyan-300 transition-colors duration-200"
                >
                  <Phone className="h-5 w-5 text-cyan-400" />
                  <span className="text-sm">{doctorInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${doctorInfo.email}`}
                  className="flex items-center space-x-2 text-gray-300 hover:text-cyan-300 transition-colors duration-200"
                >
                  <Mail className="h-5 w-5 text-cyan-400" />
                  <span className="text-sm">{doctorInfo.email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} Dr. Sarah Mitchell. All rights reserved. Made with{" "}
              <Heart className="inline h-4 w-4 text-red-500 fill-red-500 animate-pulse" />{" "}
              for better health.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <button className="hover:text-cyan-300 transition-colors duration-200">
                Privacy Policy
              </button>
              <button className="hover:text-cyan-300 transition-colors duration-200">
                Terms of Service
              </button>
              <button className="hover:text-cyan-300 transition-colors duration-200">
                HIPAA Notice
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
